import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, MapPin, Activity, Home, Car, Briefcase, 
  Brain, Scale, Shield, Radio, Smartphone, Mail, Tv, 
  MessageCircle, Heart, Zap, Lock, Eye, X, ChevronRight,
  Target, Megaphone, Split, Wifi, BarChart3, GraduationCap,
  TrendingUp, Leaf, Anchor, BookOpen, Hash, Globe, Coffee,
  MonitorPlay, ShoppingBag, MessageSquare, Gauge, AlertTriangle,
  Lightbulb, Fingerprint, Sparkles, Quote
} from 'lucide-react';

// ====================================================================================
// 1. DATA IMPORTS
// ====================================================================================
import cohort1824 from '../../../../data/demographics/cohort_summary_18_24.json';
import cohort2534 from '../../../../data/demographics/cohort_summary_25_34.json';
import cohort3544 from '../../../../data/demographics/cohort_summary_35_44.json';
import cohort4554 from '../../../../data/demographics/cohort_summary_45_54.json';
import cohort5564 from '../../../../data/demographics/cohort_summary_55_64.json';
import cohort6475 from '../../../../data/demographics/cohort_summary_64_75.json';
import cohort75plus from '../../../../data/demographics/cohort_summary_75_plus.json';

const COHORT_LOOKUP = {
  '18-24': cohort1824,
  '25-34': cohort2534,
  '35-44': cohort3544,
  '45-54': cohort4554,
  '55-64': cohort5564,
  '64-75': cohort6475,
  '75+':   cohort75plus
};

// ====================================================================================
// 2. CONSTANTS & UTILITIES
// ====================================================================================

const AGE_BUCKETS = ['18-24', '25-34', '35-44', '45-54', '55-64', '64-75', '75+'];

const PARTY_COLORS = {
  labour: '#E4003B',
  conservative: '#0087DC',
  reform: '#12B6CF',
  libdem: '#FAA61A',
  green: '#6AB023',
  other: '#9ca3af'
};

const getPartyColor = (propensity) => {
  if (!propensity) return PARTY_COLORS.other;
  const entries = Object.entries(propensity);
  if (entries.length === 0) return PARTY_COLORS.other;
  const top = entries.sort(([,a], [,b]) => b - a)[0];
  const key = top[0].toLowerCase().replace('liberal democrats', 'libdem');
  
  if (key.includes('lab')) return PARTY_COLORS.labour;
  if (key.includes('con')) return PARTY_COLORS.conservative;
  if (key.includes('ref')) return PARTY_COLORS.reform;
  if (key.includes('lib')) return PARTY_COLORS.libdem;
  if (key.includes('gre')) return PARTY_COLORS.green;
  return PARTY_COLORS.other;
};

const getChannelIcon = (c) => {
    const k = (c || '').toLowerCase();
    if (k.includes('facebook') || k.includes('instagram') || k.includes('tiktok') || k.includes('social')) return <Smartphone size={14} />;
    if (k.includes('radio') || k.includes('podcast')) return <Radio size={14} />;
    if (k.includes('tv') || k.includes('news')) return <Tv size={14} />;
    if (k.includes('paper') || k.includes('mail') || k.includes('letter') || k.includes('post')) return <Mail size={14} />;
    if (k.includes('whatsapp') || k.includes('nextdoor') || k.includes('group')) return <MessageCircle size={14} />;
    return <Zap size={14} />;
};

const getQualitativeScore = (score) => {
    if (score > 0.75) return "Very High";
    if (score > 0.6) return "High";
    if (score > 0.4) return "Moderate";
    if (score > 0.25) return "Low";
    return "Negligible";
};

// ====================================================================================
// 3. ENRICHMENT LOGIC
// ====================================================================================

const getEnrichedVariant = (variant) => {
    if (!variant) return null;
    
    const key = variant.category_key; 
    if (!key) return variant;

    // Scan Static Library
    const allCohorts = Object.values(COHORT_LOOKUP).flat();
    let richProfile = null;
    let cohortRange = null;

    for (const cohortGroup of allCohorts) {
        if (cohortGroup.profiles && cohortGroup.profiles[key]) {
            richProfile = cohortGroup.profiles[key];
            cohortRange = cohortGroup.age_range;
            break;
        }
    }

    if (richProfile) {
        return {
            ...variant,
            display_age_range: cohortRange || variant.age_range?.target,
            paragraph_summary: richProfile.paragraph_summary || variant.description,
            cultural_designator: richProfile.cultural_designator || variant.cultural_designator,
            sociolect_markers: richProfile.sociolect_markers || [],
            policy_red_lines: richProfile['2026_policy_red_lines'] || richProfile.policy_red_lines || [],
            cultural_habitus: richProfile.cultural_habitus || {},
            media_ecosystem: richProfile.media_ecosystem || {},
            psychographic_drivers: richProfile.psychographic_drivers || {},
            cognitive_architecture: richProfile.cognitive_architecture || {},
            strategic_targeting: richProfile.strategic_targeting_metrics || {},
            static_profile_data: richProfile 
        };
    }
    return variant;
};

// ====================================================================================
// 4. PSYCHOLOGICAL & LIFESTYLE ENGINES
// ====================================================================================

const PsychologicalDeepDive = ({ variant }) => {
    const mft = variant.psychographics?.mft_profile || {};
    const ocean = variant.psychographics?.ocean_profile || {};
    const cog = variant.cognitive_architecture || {};
    const constructs = variant.psychographics?.psych_constructs || {};
    const ideological = variant.psychographics?.ideological_drivers || {};
    
    // Synthesize the "Psychologist's Voice"
    const getPsychNarrative = () => {
        const drivers = [];
        if (mft.authority_subversion > 0.6) drivers.push("a profound respect for hierarchy and established order");
        if (mft.care_harm > 0.65) drivers.push("hyper-sensitivity to the suffering of vulnerable groups");
        if (mft.purity_sanctity > 0.6) drivers.push("a visceral disgust response to cultural degradation");
        if (mft.fairness_cheating > 0.65) drivers.push("a transactional view of justice—'you get what you put in'");
        
        const cognitive = constructs.need_cognitive_closure > 0.6 
            ? "Cognitively, this subject rejects ambiguity, seeking binary 'good vs evil' narratives." 
            : "Cognitively, they are comfortable with nuance and trade-offs.";
            
        return `This profile is driven by ${drivers.join(" and ")}. ${cognitive} Trust is ${constructs.institutional_trust > 0.5 ? "vested in institutions" : "highly localized or non-existent"}.`;
    };

    const ProgressBar = ({ label, value, color }) => (
        <div className="mb-2">
            <div className="flex justify-between text-[10px] uppercase font-bold text-slate-400 mb-0.5">
                <span>{label}</span>
                <span>{(value * 100).toFixed(0)}</span>
            </div>
            <div className="h-1.5 w-full bg-slate-800 rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${color}`} style={{ width: `${value * 100}%` }} />
            </div>
        </div>
    );

    return (
        <div className="space-y-6">
            {/* 1. The Synthesis */}
            <div className="bg-slate-800/40 p-4 rounded-xl border border-slate-700/50 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-1 h-full bg-indigo-500"></div>
                <h4 className="text-xs font-bold text-indigo-400 uppercase tracking-widest mb-2 flex items-center gap-2">
                    <Brain size={14} /> Cognitive Architecture
                </h4>
                <p className="text-sm text-slate-200 leading-relaxed font-medium">
                    {getPsychNarrative()}
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* 2. Moral Foundations (The WHY) */}
                <div className="bg-slate-900/40 p-4 rounded-xl border border-slate-800">
                    <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4 border-b border-slate-800 pb-2">
                        Moral Matrix (MFT)
                    </h4>
                    <ProgressBar label="Care / Harm" value={mft.care_harm} color="bg-pink-500" />
                    <ProgressBar label="Fairness / Cheating" value={mft.fairness_cheating} color="bg-sky-500" />
                    <ProgressBar label="Loyalty / Betrayal" value={mft.loyalty_betrayal} color="bg-indigo-500" />
                    <ProgressBar label="Authority / Subversion" value={mft.authority_subversion} color="bg-emerald-500" />
                    <ProgressBar label="Purity / Sanctity" value={mft.purity_sanctity} color="bg-amber-500" />
                </div>

                {/* 3. Personality (The WHO) */}
                <div className="bg-slate-900/40 p-4 rounded-xl border border-slate-800">
                    <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4 border-b border-slate-800 pb-2">
                        Big 5 Traits (OCEAN)
                    </h4>
                    <ProgressBar label="Openness" value={ocean.openness} color="bg-purple-500" />
                    <ProgressBar label="Conscientiousness" value={ocean.conscientiousness} color="bg-blue-500" />
                    <ProgressBar label="Extraversion" value={ocean.extraversion} color="bg-orange-500" />
                    <ProgressBar label="Agreeableness" value={ocean.agreeableness} color="bg-green-500" />
                    <ProgressBar label="Neuroticism (Threat)" value={ocean.neuroticism} color="bg-red-500" />
                </div>
            </div>

            {/* 4. Cognitive Levers */}
            <div className="grid grid-cols-3 gap-3">
                <div className="bg-slate-800/30 p-3 rounded-lg border border-slate-700/50">
                    <div className="text-[10px] text-slate-500 uppercase font-bold mb-1">Need for Closure</div>
                    <div className="text-lg font-bold text-white">
                        {(constructs.need_cognitive_closure * 100).toFixed(0)}<span className="text-xs text-slate-500">%</span>
                    </div>
                    <div className="text-[9px] text-slate-400 mt-1 leading-tight">
                        {constructs.need_cognitive_closure > 0.6 ? "Demands binary answers." : "Tolerates ambiguity."}
                    </div>
                </div>
                <div className="bg-slate-800/30 p-3 rounded-lg border border-slate-700/50">
                    <div className="text-[10px] text-slate-500 uppercase font-bold mb-1">System Trust</div>
                    <div className="text-lg font-bold text-white">
                        {(constructs.institutional_trust * 100).toFixed(0)}<span className="text-xs text-slate-500">%</span>
                    </div>
                    <div className="text-[9px] text-slate-400 mt-1 leading-tight">
                        {constructs.institutional_trust < 0.4 ? "Anti-establishment." : "Institutionalist."}
                    </div>
                </div>
                <div className="bg-slate-800/30 p-3 rounded-lg border border-slate-700/50">
                    <div className="text-[10px] text-slate-500 uppercase font-bold mb-1">Auth. Aggression (RWA)</div>
                    <div className="text-lg font-bold text-white">
                        {(ideological.rwa_score * 100).toFixed(0)}<span className="text-xs text-slate-500">%</span>
                    </div>
                    <div className="text-[9px] text-slate-400 mt-1 leading-tight">
                        {ideological.rwa_score > 0.6 ? "Enforce order." : "Question authority."}
                    </div>
                </div>
            </div>
        </div>
    );
};

// ====================================================================================
// 5. SOCIOLOGICAL ENGINE (WARD LEVEL)
// ====================================================================================

const generateSociologicalProfile = (census) => {
  const safeCensus = census || {};
  const { economic_precarity_index = 0, social_grade_index = 0, rurality_index = 0, faith_community_strength = 0, owner_occupied_pct = 0, work_from_home_pct = 0 } = safeCensus;
  const hasData = safeCensus.predominant_social_grade || safeCensus.population_density || safeCensus.avg_age;

  if (!hasData) return { headline: "Awaiting Demographic Signal...", narrative: "The system is currently aggregating census layers." };

  let headline = "";
  let narrative = [];

  if (economic_precarity_index > 0.6) {
    headline = "The Fractured Periphery";
    narrative.push("This ward defines the 'left-behind' narrative. Characterized by acute economic fragility and low institutional trust, the electorate here operates in a defensive psychological mode.");
  } else if (owner_occupied_pct > 0.75 && economic_precarity_index < 0.2) {
    headline = "The Insulated Assetocracy";
    narrative.push("A stronghold of homeowners insulated from cost-of-living shocks. The primary psychological driver here is 'loss aversion': protecting equity and preserving local character.");
  } else if (social_grade_index > 1.2) { 
    headline = "The Professional Citadel";
    narrative.push("A hub of the 'knowledge economy' elite. Economically secure but increasingly culturally distinct—prioritizing cosmopolitan values over traditional loyalties.");
  } else {
    headline = "The Squeezed Middle Ground";
    narrative.push("The classic 'battler' demographic. Navigating the friction between aspiring to security and the reality of stagnant real wages. This is the electoral fulcrum.");
  }

  if (rurality_index > 0.7) {
    narrative.push(`Socially, this is a ${faith_community_strength > 0.6 ? "tightly knit" : "atomized"} rural deep-layer. ${work_from_home_pct > 0.25 ? "However, the influx of remote-working professionals is creating a 'two-speed' village economy." : "Connectivity remains the singular unifying grievance."}`);
  } else if (rurality_index < 0.3) {
     narrative.push("Urban density here fosters a 'hyper-networked' electorate, yet social isolation remains a paradox.");
  }

  return { headline, narrative: narrative.join(" ") };
};

// ====================================================================================
// 6. UI COMPONENTS (SMALL)
// ====================================================================================

const SocialMetric = ({ icon: Icon, label, value, context, intensity }) => {
    const colors = {
        good: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
        neutral: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
        alert: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
        critical: 'text-rose-400 bg-rose-500/10 border-rose-500/20',
        empty: 'text-slate-600 bg-slate-800/50 border-slate-700/50'
    };
    return (
        <div className={`flex items-start p-3 rounded-lg border ${colors[intensity] || colors.neutral} transition-all h-full`}>
            <div className="mt-0.5 mr-3">
                <Icon size={16} />
            </div>
            <div>
                <div className="text-[10px] font-bold uppercase tracking-widest opacity-80 mb-0.5">{label}</div>
                <div className="text-lg font-bold leading-none mb-1 text-slate-200">{value || "-"}</div>
                <div className="text-[10px] opacity-70 leading-tight">{context || "No Data"}</div>
            </div>
        </div>
    )
};

const ArchetypeCard = ({ variant, onClick }) => {
    const v = variant ?? {};
    const partyColor = getPartyColor(v.political_profile?.party_propensity);
    const mft = v.psychographics?.mft_profile || {}; 
    const topMoral = Object.entries(mft).sort(([,a], [,b]) => b-a)[0]; 

    return (
        <motion.div 
            layout
            onClick={onClick}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="group relative bg-slate-900/40 border border-slate-700/50 hover:border-indigo-500/50 hover:bg-slate-800/60 rounded-xl overflow-hidden transition-all duration-300 cursor-pointer flex flex-col"
        >
            <div className="px-4 py-3 border-b border-slate-700/50 bg-slate-800/30 flex justify-between items-center group-hover:bg-slate-800/50 transition-colors">
                <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-slate-700 text-slate-300 border border-slate-600">
                        {v.category_key?.split('_')[1] || "PROFILE"}
                    </span>
                    <span className="text-[10px] text-slate-500 font-mono">
                        ~{v.population_metrics?.estimated_population || 0} Residents
                    </span>
                </div>
                <div className="flex items-center gap-2">
                     <span className="text-[9px] text-slate-500 uppercase font-bold group-hover:text-indigo-400 transition-colors">View Dossier</span>
                     <ChevronRight size={14} className="text-slate-600 group-hover:text-indigo-400" />
                </div>
            </div>

            <div className="p-4 space-y-3 flex-1 flex flex-col justify-between">
                <div className="flex justify-between items-start">
                    <div>
                        <h4 className="text-base font-bold text-slate-100 group-hover:text-white transition-colors">
                            {v.name || "Unknown Segment"}
                        </h4>
                        <div className="text-xs font-medium text-indigo-400 italic mt-0.5">
                            "{v.cultural_designator || "N/A"}"
                        </div>
                    </div>
                    <div className="w-2.5 h-2.5 rounded-full shadow-[0_0_8px_currentColor]" style={{ color: partyColor, backgroundColor: partyColor }} />
                </div>

                <div className="bg-slate-950/30 rounded-lg p-2.5 border border-slate-800/50 flex items-center gap-3">
                    <Brain size={14} className="text-slate-500 flex-shrink-0" />
                    <div className="flex flex-col">
                        <span className="text-[9px] text-slate-500 uppercase font-bold">Primary Driver</span>
                        <span className="text-[10px] text-slate-300 capitalize truncate">
                             {topMoral ? topMoral[0].replace(/_/g, ' ') : "Unmapped"}
                        </span>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

// --- THE DEEP DOSSIER VIEW ---
const ExpandedVariantView = ({ variant, onClose }) => {
    const [activeTab, setActiveTab] = useState('overview');
    
    // *** HYDRATION POINT ***
    const enrichedVariant = useMemo(() => getEnrichedVariant(variant), [variant]);
    
    if (!enrichedVariant) return null;

    const ctx = enrichedVariant.context || {};
    const pol = enrichedVariant.political_profile || {};
    const targeting = enrichedVariant.campaign_targeting || {};
    const aio = enrichedVariant.psychographics?.aio_profile || {};
    const vals = enrichedVariant.psychographics?.vals_profile || {};
    
    // Rich Data Fields
    const narrativeSummary = enrichedVariant.paragraph_summary || enrichedVariant.description || "No narrative summary available.";
    const sociolects = enrichedVariant.sociolect_markers || [];
    const habitus = enrichedVariant.cultural_habitus || {};
    const media = enrichedVariant.media_ecosystem || {};
    const redLines = enrichedVariant.policy_red_lines || [];
    const ageRangeDisplay = enrichedVariant.display_age_range || enrichedVariant.age_range?.target || "N/A";

    const tabs = [
        { id: 'overview', label: 'Mission Control', icon: Gauge },
        { id: 'psych', label: 'Psychology', icon: Brain },
        { id: 'lifestyle', label: 'Lifestyle', icon: Coffee },
        { id: 'politics', label: 'Politics', icon: Scale },
    ];

    return (
        <motion.div 
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 50 }}
            className="absolute inset-0 z-20 bg-slate-900 border border-slate-700/50 rounded-2xl overflow-hidden flex flex-col shadow-2xl"
        >
            {/* Header */}
            <div className="p-5 border-b border-slate-700 bg-slate-800/50 flex justify-between items-start flex-shrink-0">
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] font-bold uppercase tracking-widest bg-indigo-500/20 text-indigo-300 px-2 py-0.5 rounded border border-indigo-500/30">
                            {enrichedVariant.category_key || "VARIANT"}
                        </span>
                        <span className="text-[10px] font-bold uppercase tracking-widest bg-slate-700 text-slate-300 px-2 py-0.5 rounded border border-slate-600">
                            Age: {ageRangeDisplay}
                        </span>
                        <span className="text-xs text-slate-400 font-mono ml-1">
                            Pop: {(enrichedVariant.population_metrics?.estimated_population || 0).toLocaleString()}
                        </span>
                    </div>
                    <h2 className="text-2xl font-bold text-white leading-none mb-1">{enrichedVariant.name}</h2>
                    <div className="text-sm text-sky-400 italic">"{enrichedVariant.cultural_designator}"</div>
                </div>
                <button onClick={onClose} className="p-2 hover:bg-slate-700 rounded-full text-slate-400 hover:text-white transition-colors">
                    <X size={20} />
                </button>
            </div>

            {/* TABS */}
            <div className="flex items-center space-x-1 px-4 border-b border-slate-700/50 relative overflow-x-auto bg-slate-900/80 backdrop-blur-sm">
                {tabs.map((tab) => {
                    const isActive = activeTab === tab.id;
                    const Icon = tab.icon;
                    return (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`
                                relative py-3 px-3 flex items-center space-x-2 text-[10px] md:text-xs font-bold uppercase tracking-widest transition-all duration-300 outline-none group whitespace-nowrap
                                ${isActive ? 'text-white' : 'text-slate-500 hover:text-slate-300'}
                            `}
                        >
                            <Icon size={14} className={`transition-all duration-300 ${isActive ? "text-white drop-shadow-[0_0_10px_rgba(255,255,255,0.8)]" : "group-hover:text-slate-300"}`} />
                            <span className={`block ${isActive ? "drop-shadow-[0_0_10px_rgba(255,255,255,0.6)]" : ""}`}>{tab.label}</span>
                            {isActive && <motion.div layoutId="activeDossierTab" className="absolute bottom-0 left-0 right-0 h-[2px] bg-white shadow-[0_0_10px_1px_rgba(255,255,255,0.5)]" />}
                        </button>
                    );
                })}
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto custom-scrollbar p-6 bg-slate-900/50">
                
                {/* TAB 1: MISSION CONTROL */}
                {activeTab === 'overview' && (
                    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2">
                        <div className="bg-slate-800/30 p-4 rounded-xl border border-slate-700/50">
                            <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Narrative Profile</h4>
                            <p className="text-sm text-slate-200 leading-relaxed">
                                {narrativeSummary}
                            </p>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-4">
                                <div className="p-3 bg-slate-950/30 border border-slate-800 rounded-lg">
                                    <div className="text-[10px] text-slate-500 uppercase mb-1">Employment</div>
                                    <div className="text-sm text-white capitalize">{ctx.employment_status?.replace(/_/g, ' ') || "N/A"}</div>
                                </div>
                                <div className="p-3 bg-slate-950/30 border border-slate-800 rounded-lg">
                                    <div className="text-[10px] text-slate-500 uppercase mb-1">Household</div>
                                    <div className="text-sm text-white capitalize">{ctx.household_composition?.replace(/_/g, ' ') || "N/A"}</div>
                                </div>
                            </div>
                            <div className="space-y-4">
                                <div className="p-3 bg-slate-950/30 border border-slate-800 rounded-lg">
                                    <div className="text-[10px] text-slate-500 uppercase mb-1">Social Grade</div>
                                    <div className="text-sm text-white font-bold">{ctx.social_grade || "N/A"}</div>
                                </div>
                                <div className="p-3 bg-slate-950/30 border border-slate-800 rounded-lg">
                                    <div className="text-[10px] text-slate-500 uppercase mb-1">Location Type</div>
                                    <div className="text-sm text-white capitalize">{ctx.location_type || "N/A"}</div>
                                </div>
                            </div>
                        </div>

                         {/* Red Lines */}
                         {redLines.length > 0 && (
                            <div className="p-4 bg-rose-950/20 border border-rose-500/20 rounded-xl">
                                <h4 className="text-xs font-bold text-rose-400 uppercase tracking-widest mb-2 flex items-center gap-2">
                                    <AlertTriangle size={14} /> Strategic Red Lines
                                </h4>
                                <ul className="space-y-1">
                                    {redLines.map((line, i) => (
                                        <li key={i} className="text-xs text-rose-200 flex items-start gap-2">
                                            <span className="mt-1.5 w-1 h-1 rounded-full bg-rose-500 flex-shrink-0"></span>
                                            {line}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <div className="p-4 bg-indigo-950/20 border border-indigo-500/20 rounded-xl">
                            <div className="flex justify-between items-center mb-2">
                                <h4 className="text-xs font-bold text-indigo-400 uppercase tracking-widest">VALS Segment</h4>
                                <span className="text-[10px] bg-indigo-500/20 text-indigo-300 px-2 py-0.5 rounded border border-indigo-500/30 uppercase">
                                    {vals.primary_motivation || "Unknown"}
                                </span>
                            </div>
                            <div className="text-lg font-bold text-white">{vals.segment || "Unclassified"}</div>
                            <div className="text-xs text-indigo-300/60 mt-1 capitalize">Resources: {vals.resources}</div>
                        </div>
                    </div>
                )}

                {/* TAB 2: PSYCHOLOGY (REBUILT DEEP DIVE) */}
                {activeTab === 'psych' && (
                    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2">
                         <PsychologicalDeepDive variant={enrichedVariant} />
                    </div>
                )}

                {/* TAB 3: LIFESTYLE (REBUILT RICH VIEW) */}
                {activeTab === 'lifestyle' && (
                    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2">
                        {/* 1. SOCIOLECTS */}
                        <div className="bg-slate-950/30 p-4 rounded-xl border border-slate-800">
                            <h4 className="text-xs font-bold text-indigo-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                                <MessageSquare size={14} /> Vernacular & Sociolects
                            </h4>
                            <div className="flex flex-wrap gap-2">
                                {sociolects.length > 0 ? sociolects.map((marker, i) => (
                                    <div key={i} className="px-3 py-1.5 bg-slate-900 border border-slate-700 rounded-full flex items-center gap-2">
                                        <Quote size={10} className="text-slate-500" />
                                        <span className="text-xs text-slate-300 italic">"{marker}"</span>
                                    </div>
                                )) : (
                                    <span className="text-xs text-slate-500 italic">Standard vernacular. No distinct markers.</span>
                                )}
                            </div>
                        </div>

                        {/* 2. LEISURE & HABITUS (COMBINED RICH GRID) */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="bg-slate-950/30 p-4 rounded-xl border border-slate-800">
                                <h4 className="text-xs font-bold text-pink-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                                    <Coffee size={14} /> Leisure Activities
                                </h4>
                                <div className="space-y-2">
                                    {Object.entries(aio.leisure || {}).sort(([,a], [,b]) => b - a).slice(0, 5).map(([activity, score]) => (
                                        <div key={activity} className="flex justify-between items-center">
                                            <span className="text-xs text-slate-300 capitalize">{activity.replace(/_/g, ' ')}</span>
                                            <div className="flex gap-0.5">
                                                {[...Array(Math.ceil(score * 4))].map((_, i) => (
                                                    <div key={i} className="w-1 h-1 rounded-full bg-pink-500/50" />
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            
                            <div className="bg-slate-950/30 p-4 rounded-xl border border-slate-800">
                                <h4 className="text-xs font-bold text-emerald-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                                    <ShoppingBag size={14} /> Cultural Habitus
                                </h4>
                                <div className="space-y-3">
                                    {habitus.consumption_style && (
                                        <div className="text-xs text-slate-400 italic border-l-2 border-emerald-500/30 pl-2">
                                            "{habitus.consumption_style}"
                                        </div>
                                    )}
                                    <div className="flex flex-wrap gap-1.5">
                                        {(habitus.key_brands || []).map((b, i) => (
                                            <span key={i} className="text-[10px] px-2 py-1 bg-slate-900 border border-slate-700 rounded text-slate-300">
                                                {b}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 3. MEDIA ECOSYSTEM */}
                        <div className="bg-slate-950/30 p-4 rounded-xl border border-slate-800">
                             <h4 className="text-xs font-bold text-sky-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                                 <MonitorPlay size={14} /> Media Ecosystem
                             </h4>
                             <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                                 {Object.entries(aio.media || {}).sort(([,a], [,b]) => b - a).map(([chan, score]) => (
                                     <div key={chan} className="flex items-center justify-between py-1 border-b border-slate-800/50">
                                         <span className="text-xs text-white capitalize">{chan.replace(/_/g, ' ')}</span>
                                         <div className="w-16 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                                            <div className="h-full bg-sky-500" style={{ width: `${score * 100}%`}} />
                                         </div>
                                     </div>
                                 ))}
                             </div>
                        </div>
                    </div>
                )}

                {/* TAB 4: POLITICS */}
                {activeTab === 'politics' && (
                    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2">
                        <div className="bg-slate-950/30 p-4 rounded-xl border border-slate-800">
                            <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Voting Intention</h4>
                            <div className="space-y-2">
                                {Object.entries(pol.party_propensity || {})
                                .sort(([,a], [,b]) => b-a)
                                .map(([party, score]) => (
                                    <div key={party} className="flex items-center gap-3">
                                        <div className="w-24 text-[10px] text-slate-400 font-bold uppercase text-right">{party.replace(/_/g, ' ')}</div>
                                        <div className="flex-1 h-3 bg-slate-900 rounded-full overflow-hidden">
                                            <div className="h-full rounded-full" style={{ width: `${score*100}%`, backgroundColor: getPartyColor({[party]: 1}) }} />
                                        </div>
                                        <div className="w-8 text-[10px] text-slate-300 font-mono">{(score*100).toFixed(0)}%</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="bg-slate-950/30 p-4 rounded-xl border border-slate-800">
                            <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                                <Target size={14} /> Priority Issues
                            </h4>
                            <div className="flex flex-wrap gap-2">
                                {Object.entries(pol.issue_priorities || {})
                                .sort(([,a], [,b]) => b-a)
                                .map(([iss, score]) => (
                                    <div key={iss} className="px-3 py-1 bg-slate-800 border border-slate-600 rounded-full flex items-center gap-2">
                                        <span className="text-xs text-white capitalize">{iss.replace(/_/g, ' ')}</span>
                                        <div className="h-1.5 w-1.5 rounded-full bg-rose-500" style={{opacity: score}} />
                                    </div>
                                ))}
                            </div>
                        </div>

                         <div className="bg-slate-950/30 p-4 rounded-xl border border-slate-800">
                            <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                                <Megaphone size={14} /> Channel Receptivity
                            </h4>
                            <div className="grid grid-cols-2 gap-2">
                                {Object.entries(targeting.channel_receptivity || {})
                                .sort(([,a], [,b]) => b-a)
                                .map(([ch, score]) => (
                                    <div key={ch} className="flex justify-between items-center px-2 py-1 border-b border-slate-800/50">
                                        <div className="flex items-center gap-2">
                                            {getChannelIcon(ch)}
                                            <span className="text-xs text-slate-300 capitalize">{ch.replace(/_/g, ' ')}</span>
                                        </div>
                                        <div className="text-[10px] font-bold text-emerald-400">{getQualitativeScore(score)}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </motion.div>
    );
};

// ====================================================================================
// 6. MAIN COMPONENT
// ====================================================================================

export const WardDemographicsPanel = (props) => {
    const [activeCohort, setActiveCohort] = useState('all');
    const [activeGender, setActiveGender] = useState('all'); 
    const [selectedVariant, setSelectedVariant] = useState(null);

    // --- 1. DATA HANDLING ---
    const rawData = props.data || props; 
    const isRootCensus = 'active_commute_pct' in rawData || 'predominant_social_grade' in rawData;
    const census = rawData.census_summary || (isRootCensus ? rawData : {});
    const variants = Array.isArray(rawData.variants) 
        ? rawData.variants 
        : Array.isArray(census.variants)
            ? census.variants
            : []; 

    // --- 2. LOGIC ---
    const sociologicalProfile = useMemo(() => generateSociologicalProfile(census), [census]);
    const genderSplit = census.gender_split_by_age || {};
    const totalPop = Object.values(genderSplit).reduce((a,b) => a + (b.total_count||0), 0) || 1;
    
    // Calculate aggregate metrics
    const aggMetrics = useMemo(() => {
        let frictionSum = 0;
        let engagementSum = 0;
        let popSum = 0;

        variants.forEach(v => {
            const p = v.population_metrics?.estimated_population || 0;
            const friction = v.campaign_targeting?.activation_difficulty || 0.5;
            const engage = (v.political_profile?.political_engagement || 0.5);
            
            frictionSum += (friction * p);
            engagementSum += (engage * p);
            popSum += p;
        });

        return {
            friction: popSum > 0 ? frictionSum / popSum : 0.5,
            civic: popSum > 0 ? engagementSum / popSum : 0.5
        };
    }, [variants]);

    // PYRAMID DATA PREP
    const pyramidData = useMemo(() => {
        let maxSegmentSize = 0;
        AGE_BUCKETS.forEach(bucket => {
            const d = genderSplit[bucket] || { total_count: 0, male_pct: 0.5, female_pct: 0.5 };
            const mCount = d.total_count * d.male_pct;
            const fCount = d.total_count * d.female_pct;
            if (mCount > maxSegmentSize) maxSegmentSize = mCount;
            if (fCount > maxSegmentSize) maxSegmentSize = fCount;
        });

        return AGE_BUCKETS.map(bucket => {
            const bData = genderSplit[bucket] || { female_pct: 0, male_pct: 0, total_count: 0 };
            const mCount = bData.total_count * bData.male_pct;
            const fCount = bData.total_count * bData.female_pct;
            
            return {
                bucket,
                maleWidth: maxSegmentSize > 0 ? (mCount / maxSegmentSize) * 100 : 0,
                femaleWidth: maxSegmentSize > 0 ? (fCount / maxSegmentSize) * 100 : 0,
                maleCount: mCount,
                femaleCount: fCount
            };
        });
    }, [genderSplit]);

    // DEMOGRAPHIC STATS FOR FOOTER
    const demographicStats = useMemo(() => {
        let total = 0;
        let workingAge = 0;
        let dependents = 0;
        let maleTotal = 0;
        let femaleTotal = 0;

        AGE_BUCKETS.forEach(b => {
            const d = genderSplit[b];
            if (d) {
                const c = d.total_count || 0;
                total += c;
                if (b === '64-75' || b === '75+') dependents += c; // Simplified dependency (Elderly)
                else workingAge += c;

                const mPct = d.male_pct || 0.5;
                const fPct = d.female_pct || 0.5;
                maleTotal += (c * mPct);
                femaleTotal += (c * fPct);
            }
        });

        const depRatio = workingAge > 0 ? (dependents / workingAge) * 100 : 0;
        
        return {
            total,
            depRatio: depRatio.toFixed(1),
            malePct: total > 0 ? Math.round((maleTotal/total)*100) : 50,
            femalePct: total > 0 ? Math.round((femaleTotal/total)*100) : 50
        }
    }, [genderSplit]);

    const visibleVariants = useMemo(() => {
        if (variants.length === 0) return [];
        let filtered = variants;
        if (activeCohort !== 'all') {
            const [min, maxStr] = activeCohort.split('-');
            const minAge = parseInt(min);
            const maxAge = maxStr === undefined ? 100 : parseInt(maxStr);
            filtered = filtered.filter(v => {
                const age = v.age_range?.target || v.avg_age || 0;
                return age >= minAge && age <= maxAge;
            });
        }
        if (activeGender !== 'all') {
            filtered = filtered.filter(v => {
                if (v.gender_key) return v.gender_key[0].includes(activeGender[0]);
                return true; 
            });

            console.hidden(filtered.map((v) => v.gender_key[0]))
        }
        return filtered.sort((a,b) => (b.population_metrics?.estimated_population || 0) - (a.population_metrics?.estimated_population || 0));
    }, [variants, activeCohort, activeGender]);

    return (
        <div className="space-y-6 animate-in fade-in duration-500 w-full relative">
            
            {/* 1. KEY METRICS */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <SocialMetric 
                    icon={Scale} label="Social Grade" 
                    value={census.predominant_social_grade || "N/A"} 
                    context={census.social_grade_index ? `Index: ${census.social_grade_index.toFixed(2)}` : "Index Pending"}
                    intensity={census.predominant_social_grade ? "neutral" : "empty"}
                />
                <SocialMetric 
                    icon={Activity} label="Precarity" 
                    value={census.economic_precarity_index ? (census.economic_precarity_index * 100).toFixed(0) : "0"} 
                    context="Economic Risk" intensity={census.economic_precarity_index > 0.4 ? "alert" : "good"}
                />
                <SocialMetric 
                    icon={Home} label="Tenure" 
                    value={census.owner_occupied_pct ? `${(census.owner_occupied_pct * 100).toFixed(0)}%` : "N/A"} 
                    context="Owned" intensity="neutral"
                />
                <SocialMetric 
                    icon={Shield} label="Cohesion" 
                    value={census.faith_community_strength ? (census.faith_community_strength * 100).toFixed(0) : "0"} 
                    context="Strength Index" intensity="neutral"
                />
            </div>

            {/* 2. NARRATIVE HEADER (FULL WIDTH TEXT) */}
            <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-indigo-950/40 to-slate-900 border border-indigo-500/30 p-6 shadow-lg">
                <div className="flex flex-col md:flex-row gap-5 items-start relative z-10">
                    <div className="p-2.5 bg-indigo-500/20 rounded-lg border border-indigo-500/30 text-indigo-400 flex-shrink-0">
                        <Brain size={20} />
                    </div>
                    <div className="space-y-1.5 w-full">
                        <div className="flex items-center gap-3">
                            <h3 className="text-base font-bold text-white tracking-tight">
                                {sociologicalProfile.headline}
                            </h3>
                            <span className="px-1.5 py-0.5 bg-indigo-500/20 text-indigo-300 text-[9px] font-bold uppercase rounded border border-indigo-500/30">
                                AI Analysis
                            </span>
                        </div>
                        <p className="text-sm text-slate-300 leading-relaxed w-full">
                            {sociologicalProfile.narrative}
                        </p>
                    </div>
                </div>
            </div>

            {/* 3. FILTER TOOLBAR */}
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 py-2 border-b border-slate-800">
                <div className="flex items-center gap-3">
                     <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">Filters:</span>
                     
                     <div className="flex bg-slate-900 p-0.5 rounded-lg border border-slate-700">
                        {['all', 'female', 'male'].map(g => (
                            <button 
                                key={g}
                                onClick={() => setActiveGender(g)} 
                                className={`
                                    px-3 py-1 text-[10px] font-bold uppercase tracking-wider rounded-md transition-all flex items-center gap-2
                                    ${activeGender === g 
                                        ? (g==='female'?'bg-pink-600':g==='male'?'bg-blue-600':'bg-indigo-600') + ' text-white shadow-sm' 
                                        : 'text-slate-400 hover:text-white hover:bg-slate-800'
                                    }
                                `}
                            >
                                {g === 'all' ? 'All' : g === 'female' ? 'Women' : 'Men'}
                            </button>
                        ))}
                    </div>

                    {activeCohort !== 'all' && (
                        <button 
                            onClick={() => setActiveCohort('all')}
                            className="text-[10px] font-bold text-indigo-400 hover:text-indigo-300 bg-indigo-500/10 px-3 py-1 rounded transition-all flex items-center gap-1"
                        >
                            <X size={10} /> Reset Cohort
                        </button>
                    )}
                </div>
                
                <span className="text-[10px] text-slate-500 font-mono">
                    Showing {visibleVariants.length} variants
                </span>
            </div>

            {/* 4. MAIN CONTENT GRID */}
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 min-h-[600px]">
                
                {/* LEFT: POPULATION PYRAMID */}
                <div className="xl:col-span-4 bg-slate-900/30 border border-slate-700/50 rounded-2xl p-6 flex flex-col h-full min-h-[600px] overflow-hidden">
                    <h4 className="text-sm font-bold text-slate-200 mb-6 flex items-center gap-2">
                        <Users size={16} className="text-slate-400" />
                        Population Architecture
                    </h4>
                    
                    {/* PYRAMID CHART CONTAINER */}
                    <div className="flex-1 space-y-2 overflow-hidden flex flex-col justify-center">
                        <div className="flex justify-between text-[10px] font-bold text-slate-500 uppercase px-2 mb-2">
                            <span className={activeGender === 'female' ? 'opacity-30' : 'opacity-100'}>Male</span>
                            <span>Cohort</span>
                            <span className={activeGender === 'male' ? 'opacity-30' : 'opacity-100'}>Female</span>
                        </div>

                        {pyramidData.map((bucket) => {
                            const isSelected = activeCohort === bucket.bucket;
                            const isDimmed = activeCohort !== 'all' && !isSelected;

                            return (
                                <div 
                                    key={bucket.bucket}
                                    onClick={() => setActiveCohort(isSelected ? 'all' : bucket.bucket)}
                                    className={`relative group cursor-pointer transition-all duration-300 ${isDimmed ? 'opacity-30 blur-[1px]' : 'opacity-100'}`}
                                >
                                    <div className="flex items-center h-8 relative z-10">
                                        {/* Male Bar (Right Aligned in Left Box) */}
                                        <div className="flex-1 flex justify-end pr-3 border-r border-slate-700/50">
                                            <div className="relative h-full flex items-center justify-end w-full">
                                                <motion.div 
                                                    initial={{ width: 0 }}
                                                    animate={{ width: `${bucket.maleWidth}%` }}
                                                    className={`h-5 rounded-l-sm transition-colors duration-300 ${isSelected ? 'bg-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.5)]' : 'bg-blue-500/80 group-hover:bg-blue-400'} ${activeGender === 'female' ? 'opacity-20' : 'opacity-100'}`}
                                                />
                                            </div>
                                        </div>
                                        
                                        {/* Label */}
                                        <div className="w-14 text-center text-xs font-bold text-slate-400 group-hover:text-white transition-colors">
                                            {bucket.bucket}
                                        </div>
                                        
                                        {/* Female Bar (Left Aligned in Right Box) */}
                                        <div className="flex-1 pl-3 border-l border-slate-700/50">
                                            <div className="relative h-full flex items-center w-full">
                                                <motion.div 
                                                    initial={{ width: 0 }}
                                                    animate={{ width: `${bucket.femaleWidth}%` }}
                                                    className={`h-5 rounded-r-sm transition-colors duration-300 ${isSelected ? 'bg-pink-500 shadow-[0_0_10px_rgba(236,72,153,0.5)]' : 'bg-pink-500/80 group-hover:bg-pink-400'} ${activeGender === 'male' ? 'opacity-20' : 'opacity-100'}`}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    {isSelected && <div className="absolute inset-0 bg-white/5 rounded pointer-events-none" />}
                                </div>
                            );
                        })}
                    </div>

                    {/* DEMOGRAPHIC SUMMARY FOOTER */}
                    <div className="mt-auto pt-6 border-t border-slate-800">
                        <h5 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-3">Demographic Summary</h5>
                        <div className="grid grid-cols-2 gap-3">
                             <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/30">
                                <div className="text-[10px] text-slate-400 uppercase">Total Pop</div>
                                <div className="text-lg font-bold text-white">{demographicStats.total.toLocaleString()}</div>
                             </div>
                             <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/30">
                                <div className="text-[10px] text-slate-400 uppercase">Dependency</div>
                                <div className="text-lg font-bold text-white">{demographicStats.depRatio}%</div>
                             </div>
                             <div className="col-span-2 flex items-center gap-3 bg-slate-800/50 rounded-lg p-3 border border-slate-700/30">
                                 <div className="text-[10px] text-slate-400 uppercase w-12 font-bold">Split</div>
                                 <div className="flex-1 h-2 bg-slate-700 rounded-full flex overflow-hidden">
                                     <div className="bg-blue-500 h-full" style={{width: `${demographicStats.malePct}%`}} />
                                     <div className="bg-pink-500 h-full" style={{width: `${demographicStats.femalePct}%`}} />
                                 </div>
                                 <div className="text-[10px] font-bold text-slate-300 whitespace-nowrap">
                                     <span className="text-blue-400">{demographicStats.malePct}%</span> / <span className="text-pink-400">{demographicStats.femalePct}%</span>
                                 </div>
                             </div>
                        </div>
                    </div>
                </div>

                {/* RIGHT: ARCHETYPE FEED */}
                <div className="xl:col-span-8 flex flex-col h-full relative">
                    <div className="flex items-center gap-2 mb-4">
                        <Eye size={16} className="text-sky-400" />
                        <h4 className="text-sm font-bold text-slate-300 uppercase tracking-widest">
                            Dominant Demographic Variants
                        </h4>
                    </div>

                    <div className="flex-1 bg-slate-900/20 border border-slate-800 rounded-2xl p-1 overflow-hidden relative min-h-[600px]">
                        <div className="h-full overflow-y-auto custom-scrollbar p-3 space-y-3 max-h-[650px]">
                            <AnimatePresence mode="popLayout">
                                {visibleVariants.length > 0 ? (
                                    visibleVariants.map((variant, i) => (
                                        <ArchetypeCard 
                                            key={variant.variant_id || i} 
                                            variant={variant} 
                                            onClick={() => setSelectedVariant(variant)} 
                                        />
                                    ))
                                ) : (
                                    <div className="h-48 flex flex-col items-center justify-center text-slate-500 border-2 border-dashed border-slate-800 rounded-xl m-4">
                                        <Users size={32} className="mb-3 opacity-50" />
                                        <p className="text-sm font-medium">
                                            {variants.length === 0 
                                                ? "Waiting for variant data..." 
                                                : "No variants match the current filters."}
                                        </p>
                                    </div>
                                )}
                            </AnimatePresence>
                        </div>

                        <AnimatePresence>
                            {selectedVariant && (
                                <ExpandedVariantView 
                                    variant={selectedVariant} 
                                    onClose={() => setSelectedVariant(null)} 
                                />
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </div>

            {/* 5. FOOTER CONTEXT METRICS */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-6 border-t border-slate-800">
                <div className="space-y-2">
                    <h5 className="text-[10px] uppercase text-slate-500 font-bold tracking-widest flex items-center gap-2">
                        <Lock size={12} /> Identity
                    </h5>
                    <div className="bg-slate-800/30 rounded p-2 border border-slate-700/30">
                        <div className="flex justify-between text-[10px] text-slate-300 mb-1">
                            <span>Traditional</span>
                            <span className="font-mono text-white">{(census.uk_identity_strength * 100).toFixed(0)}%</span>
                        </div>
                        <div className="w-full bg-slate-700 h-1.5 rounded-full overflow-hidden">
                            <div className="bg-sky-500 h-full rounded-full" style={{ width: `${(census.uk_identity_strength || 0) * 100}%` }} />
                        </div>
                    </div>
                </div>

                <div className="space-y-2">
                    <h5 className="text-[10px] uppercase text-slate-500 font-bold tracking-widest flex items-center gap-2">
                        <Briefcase size={12} /> Work
                    </h5>
                    <div className="bg-slate-800/30 rounded p-2 border border-slate-700/30">
                        <div className="flex justify-between text-[10px] text-slate-300 mb-1">
                            <span>Remote</span>
                            <span className="font-mono text-white">{(census.work_from_home_pct * 100).toFixed(0)}%</span>
                        </div>
                        <div className="w-full bg-slate-700 h-1.5 rounded-full overflow-hidden">
                            <div className="bg-purple-500 h-full rounded-full" style={{ width: `${(census.work_from_home_pct || 0) * 100}%` }} />
                        </div>
                    </div>
                </div>

                <div className="space-y-2">
                    <h5 className="text-[10px] uppercase text-slate-500 font-bold tracking-widest flex items-center gap-2">
                        <Heart size={12} /> Health
                    </h5>
                    <div className="bg-slate-800/30 rounded p-2 border border-slate-700/30">
                        <div className="flex justify-between text-[10px] text-slate-300 mb-1">
                             <span>Health Index</span>
                             <span className={`font-mono font-bold ${census.health_index > 0.5 ? 'text-emerald-400' : 'text-rose-400'}`}>
                                 {census.health_index ? (census.health_index * 100).toFixed(0) + '%' : "-"}
                             </span>
                        </div>
                        <div className="w-full bg-slate-700 h-1.5 rounded-full overflow-hidden">
                            <div 
                                className={`h-full rounded-full ${census.health_index > 0.5 ? 'bg-emerald-500' : 'bg-rose-500'}`} 
                                style={{ width: `${(census.health_index || 0) * 100}%` }} 
                            />
                        </div>
                    </div>
                </div>

                <div className="space-y-2">
                    <h5 className="text-[10px] uppercase text-slate-500 font-bold tracking-widest flex items-center gap-2">
                        <Wifi size={12} /> Friction
                    </h5>
                    <div className="bg-slate-800/30 rounded p-2 border border-slate-700/30">
                        <div className="flex justify-between text-[10px] text-slate-300 mb-1">
                            <span>Reach Difficulty</span>
                            <span className="font-mono text-white">{(aggMetrics.friction * 100).toFixed(0)}%</span>
                        </div>
                         <div className="w-full bg-slate-700 h-1.5 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${aggMetrics.friction > 0.6 ? 'bg-rose-500' : 'bg-emerald-500'}`} style={{ width: `${aggMetrics.friction * 100}%` }} />
                        </div>
                    </div>
                </div>

                <div className="space-y-2">
                    <h5 className="text-[10px] uppercase text-slate-500 font-bold tracking-widest flex items-center gap-2">
                        <BarChart3 size={12} /> Civic Pulse
                    </h5>
                    <div className="bg-slate-800/30 rounded p-2 border border-slate-700/30">
                        <div className="flex justify-between text-[10px] text-slate-300 mb-1">
                            <span>Engagement</span>
                            <span className="font-mono text-white">{(aggMetrics.civic * 100).toFixed(0)}%</span>
                        </div>
                         <div className="w-full bg-slate-700 h-1.5 rounded-full overflow-hidden">
                            <div className="bg-amber-500 h-full rounded-full" style={{ width: `${aggMetrics.civic * 100}%` }} />
                        </div>
                    </div>
                </div>

                <div className="space-y-2">
                    <h5 className="text-[10px] uppercase text-slate-500 font-bold tracking-widest flex items-center gap-2">
                        <GraduationCap size={12} /> Education
                    </h5>
                    <div className="bg-slate-800/30 rounded p-2 border border-slate-700/30">
                        <div className="flex justify-between text-[10px] text-slate-300 mb-1">
                            <span>Graduate %</span>
                            <span className="font-mono text-white">{(census.graduate_percentage * 100).toFixed(0)}%</span>
                        </div>
                         <div className="w-full bg-slate-700 h-1.5 rounded-full overflow-hidden">
                            <div className="bg-cyan-500 h-full rounded-full" style={{ width: `${(census.graduate_percentage || 0) * 100}%` }} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};